import React, { createContext, useContext, useState, useEffect } from 'react';
import { GameAccount } from '../../authentication/Authentication.model';
import { useConfig } from '../../ConfigContext';
import { RequestError } from '../../Error.model';
import { useSnackbar } from '../../snackbar/SnackbarContext';
import { get, post } from '../../utils/Request';
import { GameIds } from '../my-games/MyGames.model';

export enum PromotionStatus {
  Claimed = 'claimed',
  Claimable = 'claimable',
  InProgress = 'inProgress',
}

export interface Reward {
  displayName: string;
  pictureURL: string;
  gameSpecificData: {
    universalStatus: string;
    rarity: string;
  };
}

export interface PromotionData {
  promotionId: string;
  relatedToGameId: string;
  requiredGameIds?: string[];
  rewards: Reward[];
}

export interface UserData {
  status: string;
  promocode: { code: string };
}
export interface Promotion {
  promotionData: PromotionData;
  userData: UserData;
}

export interface GamePromotions {
  requiredGameId: string;
  promotions: Promotion[];
}

interface PromoContext {
  gameId: string;
  promotions: Promotion[];
  groupedPromotions: GamePromotions[];
  getPromotions: () => Promise<void>;
  claim: (promoId: string) => Promise<void>;
  activate: (provider: string, providerId: string, activateGameId: string) => Promise<void>;
}

const PromotionContext = createContext<PromoContext>({
  gameId: null,
  promotions: [],
  groupedPromotions: [],
  claim: null,
  getPromotions: null,
  activate: null,
});

export const usePromotion = (): PromoContext => useContext(PromotionContext);

const useProvidePromotion = (gameId: GameIds): PromoContext => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [groupedPromotions, setGroupedPromotions] = useState<GamePromotions[]>([]);
  const config = useConfig();
  const snackbar = useSnackbar();

  const groupPromotionsByRequiredGame = (promos: Promotion[]): GamePromotions[] => {
    const requiredGames: GamePromotions[] = [];
    promos.forEach((promo) => {
      if (!promo.promotionData.requiredGameIds || promo.promotionData.requiredGameIds.length === 0) {
        return;
      }
      const currentRequiredGame = promo.promotionData.requiredGameIds[0];
      const requiredGameIndex = requiredGames.findIndex((x) => x.requiredGameId === currentRequiredGame);

      if (requiredGameIndex === -1) {
        requiredGames.push({ requiredGameId: currentRequiredGame, promotions: [promo] });
        return;
      }
      requiredGames[requiredGameIndex].promotions.push(promo);
    });

    return requiredGames;
  };

  async function getPromotions(): Promise<void> {
    if (!gameId) {
      throw new Error('no GameId');
    }

    try {
      const promos: Promotion[] = await get(`${config.clientConfig.host}/players/me/games/${gameId}/promotions`);
      setPromotions(promos);
    } catch (e) {
      const err = e as RequestError;
      snackbar.addErrorMessage(err.code);
    }
  }

  const claim = async (promoId: string): Promise<void> => {
    try {
      const { promocode } = await post<{ promocode: string }>(
        `${config.clientConfig.host}/players/me/promotions/${promoId}/claim`,
      );
      const promos = [...promotions];
      const promoIndex = promos.findIndex((promotion) => promotion.promotionData.promotionId === promoId);
      const promo: Promotion = {
        ...promotions[promoIndex],
        userData: {
          status: PromotionStatus.Claimed,
          promocode: { code: promocode },
        },
      };
      promos[promoIndex] = promo;
      setPromotions(promos);
    } catch (e) {
      const err = e as RequestError;
      snackbar.addErrorMessage(err.code);
    }
  };

  const activate = async (provider: string, providerId: string, activateGameId: string): Promise<void> => {
    try {
      const response = await post<{ gameAccount: GameAccount, promotions: Promotion[] }>(
        `${config.clientConfig.host}/players/me/accounts/${provider}/${providerId}/games`,
        {
          data: {
            gameId: activateGameId,
            providerSpecificData: null,
          },
        },
      );

      setPromotions(response.promotions);
    } catch (e) {
      const err = e as RequestError;

      if (err.code !== 'GameNotOwned') {
        snackbar.addErrorMessage(err.code);
      } else {
        throw e;
      }
    }
  };

  useEffect(() => {
    if (gameId) {
      getPromotions();
    }
  }, [gameId]);

  useEffect(() => {
    setGroupedPromotions(groupPromotionsByRequiredGame(promotions));
  }, [promotions]);

  return {
    gameId,
    promotions,
    groupedPromotions,
    getPromotions,
    claim,
    activate,
  };
};

export function ProvidePromotion({
  children,
  gameId = null,
}: {
  children: JSX.Element | React.ReactNode;
  gameId?: GameIds | null;
}): JSX.Element {
  const promotion = useProvidePromotion(gameId);

  return <PromotionContext.Provider value={promotion}>{children}</PromotionContext.Provider>;
}
